import React from 'react';
import type { DataGridColumn } from '../DataGrid.types';
import type { AggregationResult } from '../../utils/aggregations';

interface AggregationRowProps<T extends Record<string, unknown>> {
  columns: DataGridColumn<T>[];
  aggregations: Record<string, AggregationResult>;
}

// Numeric cell renderer types for right-alignment
const NUMERIC_RENDERERS = ['currency', 'number', 'percent', 'priceChange'];

// Format value based on column type
function formatValue(value: number, column: DataGridColumn<unknown>): string {
  if (column.cellRenderer === 'currency') {
    const currency = column.cellParams?.currency || 'TRY';
    const symbols: Record<string, string> = { TRY: '₺', USD: '$', EUR: '€', GBP: '£' };
    const symbol = symbols[currency] || '₺';
    return `${symbol} ${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (column.cellRenderer === 'percent') {
    return `${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  }
  if (column.cellRenderer === 'number') {
    const decimals = column.cellParams?.decimals ?? 0;
    return value.toLocaleString('tr-TR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  }
  return value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AggregationRow<T extends Record<string, unknown>>({
  columns,
  aggregations
}: AggregationRowProps<T>): React.ReactElement {
  return (
    <tfoot>
      <tr
        style={{
          background: 'var(--color-bg-header)',
          borderTop: '2px solid var(--color-primary)'
        }}
      >
        {columns.map((column, index) => {
          const accessorKey = column.accessorKey || column.id;
          const aggregation = aggregations[accessorKey];
          const isNumeric = NUMERIC_RENDERERS.includes(column.cellRenderer || '');

          // First column without aggregation shows "TOPLAM" label
          if (index === 0 && !aggregation) {
            return (
              <td
                key={column.id}
                style={{
                  padding: '12px 14px',
                  fontSize: '12px',
                  fontWeight: 800,
                  color: 'var(--color-primary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderRight: '1px solid var(--color-border-light)',
                  borderBottom: '1px solid var(--color-border)',
                  background: 'var(--color-bg-header)'
                }}
              >
                TOPLAM
              </td>
            );
          }

          return (
            <td
              key={column.id}
              style={{
                padding: '12px 14px',
                fontSize: '13px',
                fontWeight: 700,
                fontFamily: aggregation ? 'var(--font-mono)' : 'inherit',
                fontVariantNumeric: 'tabular-nums',
                color: aggregation ? 'var(--color-primary)' : 'var(--color-text-muted)',
                textAlign: isNumeric ? 'right' : 'left',
                borderRight: '1px solid var(--color-border-light)',
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg-header)'
              }}
            >
              {aggregation ? formatValue(aggregation.value, column as DataGridColumn<unknown>) : ''}
            </td>
          );
        })}
      </tr>
    </tfoot>
  );
}
